import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { LotCardComponent } from "../../../../shared/components/lot-card/lot-card.component";
import { exportAchatArticlesExcel } from "./achat-articles-excel.utils";

export interface AchatLotDetail {
  lotId: string | null;
  lotNumero: string;
  qtyUG: number;          // Quantité en unité de gestion
  qtyUA: number;          // Quantité en unité d'achat (fournisseur)
  coefUG: number;         // UG par 1 UA
  puUG: number;           // Prix unitaire par UG
  puUA: number;           // Prix unitaire par UA
  coutTotal: number;
}

export interface AchatCommandeDetail {
  commandeId: string;
  numero: string;
  dateKey: string;
  qtyUG: number;
  qtyUA: number;
  coefUG: number;
  puUGMoyen: number;
  puUAMoyen: number;
  coutTotal: number;
  lots: AchatLotDetail[];
}

export interface AchatArticleRow {
  articleId: string;
  articleDenomination: string;
  uniteGestion: string;    // Unité de gestion article (ex: "KG", "UN")
  uniteAchat: string;      // Unité d'achat fournisseur (ex: "carton", "palette")
  qtyUG: number;
  qtyUA: number;
  coutTotal: number;
  puUGMoyen: number;
  puUAMoyen: number;
  commandes: AchatCommandeDetail[];
}

export interface AchatArticlesDialogData {
  tiersDenomination: string;
  articles: AchatArticleRow[];
}

interface Stats {
  coutTotal: number;
  coutTotalFormatted: string;
  qtyTotale: number;
  nbArticles: number;
  nbCommandes: number;
  pareto80Count: number;
}

@Component({
  selector: "vl-achat-articles-dialog",
  standalone: true,
  imports: [
    CommonModule, SharedModule, DialogHeaderExportComponent,
    DecimalPipe, MatIcon, MatTooltip,
    RadioCardComponent, LotCardComponent,
  ],
  templateUrl: "./achat-articles-dialog.component.html",
  styleUrl: "./achat-articles-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AchatArticlesDialogComponent {
  data = inject<AchatArticlesDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<AchatArticlesDialogComponent>);

  expanded = signal<Set<string>>(new Set());

  sortedArticles = computed<AchatArticleRow[]>(() =>
    [...this.data.articles].sort((a, b) => b.coutTotal - a.coutTotal),
  );

  /** Palette HSL cohérente avec le widget (doughnut) — couleur par index article. */
  private colorMap = computed<Map<string, string>>(() => {
    const sorted = this.sortedArticles();
    const base = [140, 210, 260, 275, 40, 0, 195, 220];
    const map = new Map<string, string>();
    sorted.forEach((a, i) => {
      const h = base[i % base.length] + (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      map.set(a.articleId, `hsl(${h % 360} 70% 55%)`);
    });
    return map;
  });

  colorFor(articleId: string): string {
    return this.colorMap().get(articleId) ?? "#9db8c3";
  }

  stats = computed<Stats>(() => {
    const arts = this.data.articles;
    let coutTotal = 0, qtyTotale = 0, nbCommandes = 0;
    for (const a of arts) {
      coutTotal += a.coutTotal;
      qtyTotale += a.qtyUG;
      nbCommandes += a.commandes.length;
    }
    const sortedByCout = [...arts].sort((a, b) => b.coutTotal - a.coutTotal);
    let cum = 0;
    let p80 = 0;
    for (const a of sortedByCout) {
      cum += a.coutTotal;
      p80++;
      if (cum >= coutTotal * 0.8) break;
    }
    return {
      coutTotal,
      coutTotalFormatted: formatEuroShort(coutTotal),
      qtyTotale,
      nbArticles: arts.length,
      nbCommandes,
      pareto80Count: p80,
    };
  });

  percentPart(cout: number): string {
    const total = this.stats().coutTotal || 1;
    return ((cout / total) * 100).toFixed(1);
  }

  toggle(key: string) {
    const current = new Set(this.expanded());
    current.has(key) ? current.delete(key) : current.add(key);
    this.expanded.set(current);
  }

  isOpen(key: string): boolean {
    return this.expanded().has(key);
  }

  formatDate(dateKey: string): string {
    try {
      return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
        .format(new Date(dateKey));
    } catch { return dateKey; }
  }

  exporting = signal(false);

  async exportExcel() {
    this.exporting.set(true);
    try {
      await exportAchatArticlesExcel(this.data);
    } finally {
      this.exporting.set(false);
    }
  }
}

function formatEuroShort(v: number): string {
  const n = Number(v ?? 0);
  if (!isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `${(n / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} M€`;
  if (abs >= 10_000)
    return `${(n / 1_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} k€`;
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
}
